export interface Project {
  id: string;
  title: string;
  description: string;
  techStack: string[];
  tags: string[];
  links: {
    type: 'github' | 'demo' | 'docs' | 'npm' | 'article' | 'site' | 'video' | 'docker';
    url: string;
    label: string;
  }[];
  status: 'completed' | 'in-progress' | 'concept';
  featured?: boolean;
}

export const projectsData: Project[] = [
  {
    id: 'chimera-ai-learning',
    title: 'Chimera – AI-Powered Learning Platform',
    description: 'Built adaptive learning platform using React, Redux, Django, and PostgreSQL for teams and universities. Powered by Gemini LLM for real-time mission generation, path planning, and micro-learning with domain-tuned AI agents.',
    techStack: ['React', 'Redux', 'Django', 'PostgreSQL', 'Gemini LLM', 'AI Agents'],
    tags: ['AI', 'Learning', 'Full-stack', 'LLM'],
    links: [
      { type: 'site', url: 'https://chimera-web-alpha.vercel.app/', label: 'Project Site' },
      { type: 'video', url: 'https://youtu.be/LYKzmd2w6f8', label: 'Demo Video' }
    ],
    status: 'completed',
    featured: true
  },
  {
    id: 'moodroute-gis-routing',
    title: 'MoodRoute – Intelligent GIS Routing App',
    description: 'Built full-stack mapping tool using ArcGIS API for JavaScript, WebGL, and World Route Service with real-time traffic data. Designed mood-based routing algorithm with 12 profiles and live route scoring via spatial analysis.',
    techStack: ['ArcGIS API', 'JavaScript', 'WebGL', 'Calcite Design', 'GIS'],
    tags: ['GIS', 'Routing', 'Real-time', 'Spatial Analysis'],
    links: [
      { type: 'demo', url: 'https://mood-route-git-main-rxthxnrxjs-projects.vercel.app/', label: 'Live Demo' },
    ],
    status: 'completed',
    featured: true
  },
  {
    id: 'wireless-rag-expert',
    title: 'Wireless Network Expert System (RAG)',
    description: 'Developed RAG-powered system for navigating complex wireless networking standards (5G NR, WiFi 6/6E). Combined retrieval mechanism with fine-tuned LLM, featuring Flask web interface, document parsing, ChromaDB embeddings, and containerized deployment.',
    techStack: ['Flask', 'RAG', 'ChromaDB', 'HuggingFace', 'Llama 3', 'Docker', 'PyPDF2'],
    tags: ['RAG', 'LLM', 'Document Processing', 'AI'],
    links: [
      { type: 'github', url: 'https://github.com/rxthxnrxj/RAGpdf.git', label: 'Source Code' },
      { type: 'video', url: 'https://drive.google.com/file/d/1iFYn1FZDfBwWx90_YV8XmqS52NXmmMhu/view?usp=sharing', label: 'Demo Video' },
      { type: 'docker', url: 'https://hub.docker.com/repository/docker/rxthxnrxj/ragapp/general', label: 'Docker Image' }
    ],
    status: 'completed',
    featured: true
  },
  {
    id: 'gsquare-tracking-platform',
    title: 'GSQUARE Field Executive Tracking Platform',
    description: 'Developed comprehensive tracking platform with perimeter configuration and API integration for field executive management. Features real-time mapping, automated timestamping, QR-based data entry, role-based dashboards, and Salesforce integration via secure APIs. Use username: 1234567890 and password: welcome@1',
    techStack: ['React', 'Node.js', 'PostgreSQL', 'Salesforce API', 'QR Code', 'Geolocation', 'Real-time Mapping'],
    tags: ['Enterprise', 'Tracking', 'API Integration', 'Real-time'],
    links: [
      { type: 'demo', url: 'https://www.gsqr.in/#/login', label: 'Live Demo' },
    ],
    status: 'completed'
  },
  {
    id: 'thatjob-application-tracker',
    title: 'thatJob - Job Application Tracker',
    description: 'Built comprehensive job application tracking website with AI-powered features using LLM wrappers for application insights and automation. Features application management, status tracking, analytics dashboard, and intelligent recommendations to streamline the job search process.',
    techStack: ['React', 'Express', 'Supabase', 'Bootstrap', 'LLM Wrappers', 'Vercel'],
    tags: ['Job Tracking', 'AI', 'Full-stack', 'Productivity'],
    links: [
      { type: 'demo', url: 'https://that-job-react.vercel.app/login', label: 'Live Demo' },
      { type: 'video', url: 'https://drive.google.com/file/d/1gdD_O4fRNAYjCpcEZ0LsrZcldaOTKtIf/view?usp=sharing', label: 'Feature Demo' }
    ],
    status: 'completed'
  },
  {
    id: 'funance-mobile-app',
    title: 'Funance',
    description: 'Led team to develop ML-powered mobile app using React Native and Zustand, featuring gamification, cross-platform support, push notifications, and progress tracking. Fine-tuned Google T5 on 5,000+ inputs and utilized Stable Diffusion.',
    techStack: ['React Native', 'Django', 'Zustand', 'NLP', 'Google T5', 'Stable Diffusion'],
    tags: ['Mobile', 'React Native', 'Machine Learning', 'NLP'],
    links: [
      { type: 'github', url: 'https://github.com/rxthxnrxj/funance', label: 'Repository' },
      { type: 'site', url: 'https://devpost.com/software/funance-xuklip', label: 'App Site' },
      { type: 'video', url: 'https://www.youtube.com/watch?v=_MC7kGPL73o', label: 'Video Demo' }
    ],
    status: 'completed'
  },
  {
    id: 'ethosphere-discussion',
    title: 'Ethosphere',
    description: 'Dynamic web application leveraging NLP to facilitate meaningful discussions among like-minded individuals. Implemented Redux for state management, JWT authentication, and BERT word embeddings for enhanced recommendation accuracy.',
    techStack: ['React', 'Redux', 'Django', 'BERT', 'JWT', 'NLP'],
    tags: ['NLP', 'Social', 'AI', 'React'],
    links: [
      { type: 'github', url: 'https://github.com/rxthxnrxj/ethosphere', label: 'View Code' },
      { type: 'video', url: 'https://www.youtube.com/watch?v=eButwPi-574', label: 'Feature Demo' }
    ],
    status: 'completed'
  },
  {
    id: 'porthole-pothole-detection',
    title: 'PortHole',
    description: 'Machine learning-powered navigation web app featuring pothole detection using trained YOLO v5 model. Developed seamless ReactJS UI with real-time updates and advanced pooling techniques. Won 1st place in Smart India Hackathon.',
    techStack: ['React', 'Redux', 'Django', 'YOLO v5', 'Computer Vision'],
    tags: ['Computer Vision', 'React', 'Machine Learning', 'Award'],
    links: [
      { type: 'github', url: 'https://github.com/rxthxnrxj/porthole', label: 'Source Code' },
      { type: 'article', url: 'https://ieeexplore.ieee.org/document/10503123', label: 'Research Paper' },
    ],
    status: 'completed'
  },
  {
    id: 'aidwiser-emergency-response',
    title: 'AidWiser',
    description: 'Comprehensive infrastructure connecting emergencies to responses using AI. Led team of 3 developing custom NLP pipeline with bidirectional LSTM, integrated external APIs, and implemented optimized ambulance assignment algorithm. Won 2nd place in Smart City Hackathon.',
    techStack: ['Django', 'LSTM', 'NLP', 'ML Classifiers', 'External APIs'],
    tags: ['AI', 'Emergency', 'Backend', 'Award'],
    links: [
      { type: 'site', url: 'https://aidwiserproject.web.app/', label: 'Project Site' },
    ],
    status: 'completed'
  },
  {
    id: 'ieee-research-publications',
    title: 'IEEE Research Publications',
    description: 'Published research on AI-based pothole detection and ML-powered smart parking systems. Co-authored papers at IEEE International Conference on Approaches in Technology for Social Innovation (IATMSI) and IEEE 20th India Council International Conference (INDICON).',
    techStack: ['Machine Learning', 'Computer Vision', 'Research', 'IEEE'],
    tags: ['Research', 'Publications', 'IEEE', 'AI'],
    links: [
      { type: 'article', url: 'https://ieeexplore.ieee.org/document/10503123', label: 'Springer Paper' },
      { type: 'article', url: 'https://ieeexplore.ieee.org/document/10503123', label: 'IATMSI Paper' },
      { type: 'article', url: 'https://www.researchgate.net/publication/378529005_ML_Based_Web_Application_for_Smart_Parking_System', label: 'INDICON Paper' },
    ],
    status: 'completed'
  },
  {
    id: 'dogsNshows-ecommerce',
    title: 'dogsNshows - E-commerce Website',
    description: 'Designed and single-handedly developed a full-stack e-commerce website. Led end-to-end development including branding, UI/UX, cart functionality, order management, payment gateways, and CMS. Implemented database on PostgreSQL and RDS on AWS.',
    techStack: ['React', 'Django', 'PostgreSQL', 'AWS RDS', 'Payment Gateway', 'CMS'],
    tags: ['E-commerce', 'Full-stack', 'AWS', 'PostgreSQL'],
    links: [
      { type: 'site', url: 'https://dogsnshows.herokuapp.com/', label: 'Live Store' },
      { type: 'video', url: 'https://drive.google.com/file/d/1TMMlkG0eHn5y5dwy1ZBC0W7ceUijkZue/view?usp=drive_link', label: 'Admin Walkthrough' },
      { type: 'video', url: 'https://drive.google.com/file/d/1fEvPdIg7C0rhD6t0yzmN3n6KMkYISd7o/view?usp=drive_link', label: 'User Walkthrough' }
    ],
    status: 'completed'
  }
]; 